'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ProteinCalculator() {
  const t = useTranslations("tools.proteinCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    pounds: 160,
    cm: 178,
    kg: 73,
    activity: 1.375,
    goal: 'maintain' as 'maintain' | 'loss' | 'gain'
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    successLight: '#f0fdf4',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      {
        margin: '0 auto',
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden' as const
      },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    unitToggle: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '2rem',
      flexWrap: 'wrap' as const
    },
    unitButton: (active: boolean) => getResponsiveStyle(
      {
        border: `2px solid ${active ? '#2563eb' : '#e5e7eb'}`,
        background: active ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        color: active ? 'white' : '#6b7280',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease'
      },
      { padding: '0.75rem 1.25rem', fontSize: '0.9rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' }
    ),
    formRow: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1.5rem'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        outline: 'none'
      },
      {},
      {},
      {}
    ),
    select: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        outline: 'none',
        cursor: 'pointer'
      },
      {},
      {},
      {}
    ),
    inputWithUnit: {
      position: 'relative' as const
    },
    inputUnit: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontWeight: 600,
      pointerEvents: 'none' as const
    },
    radioGroup: getResponsiveStyle(
      { display: 'flex', marginTop: '0.5rem' },
      { flexDirection: 'column' as const, gap: '1rem' },
      { flexDirection: 'row' as const, gap: '2rem' },
      { flexDirection: 'row' as const, gap: '2rem' }
    ),
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer'
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderTop: '3px solid #10b981'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: '#065f46', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultMain: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultMainLabel: getResponsiveStyle(
      { color: '#6b7280', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    resultMainValue: getResponsiveStyle(
      {
        fontWeight: 800,
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' },
      { fontSize: '3rem' }
    ),
    resultRange: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    rangeBar: {
      background: '#e5e7eb',
      height: '2rem',
      borderRadius: '1rem',
      position: 'relative' as const,
      overflow: 'hidden' as const
    },
    rangeFill: (width: number) => ({
      background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
      height: '100%',
      borderRadius: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 700,
      transition: 'width 0.5s ease',
      width: `${width}%`
    }),
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    resultCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.25rem' }
    ),
    resultCardLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem'
    },
    resultCardValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    proteinTips: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)'
      },
      { padding: '1.5rem', marginBottom: '1.5rem' },
      { padding: '2rem', marginBottom: '2rem' },
      { padding: '2.5rem', marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    faqItem: getResponsiveStyle(
      { marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2rem' }
    ),
    reference: getResponsiveStyle(
      {
        padding: '0.75rem',
        background: 'rgba(255, 255, 255, 0.4)',
        borderRadius: '0.5rem',
        marginBottom: '0.75rem',
        borderLeft: '3px solid #ff5a70'
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    link: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.3s ease'
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      let weightKg: number;
      let heightCm: number;

      if (unitSystem === 'imperial') {
        heightCm = ((inputs.feet * 12) + inputs.inches) * 2.54;
        weightKg = inputs.pounds * 0.453592;
      } else {
        heightCm = inputs.cm;
        weightKg = inputs.kg;
      }

      const weightLbs = weightKg * 2.20462;

      // Calculate BMR using Mifflin-St Jeor Equation
      const bmr = inputs.gender === 'male'
        ? (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) + 5
        : (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) - 161;

      const tdee = bmr * inputs.activity;

      // Determine protein requirements based on goal and activity
      let proteinPerKg: number;
      let minProteinPerKg: number;
      let maxProteinPerKg: number;

      if (inputs.goal === 'loss') {
        proteinPerKg = inputs.activity >= 1.55 ? 2.2 : 1.8;
        minProteinPerKg = 1.6;
        maxProteinPerKg = 2.4;
      } else if (inputs.goal === 'gain') {
        proteinPerKg = inputs.activity >= 1.55 ? 2.0 : 1.8;
        minProteinPerKg = 1.6;
        maxProteinPerKg = 2.2;
      } else {
        proteinPerKg = inputs.activity >= 1.55 ? 1.6 : 1.2;
        minProteinPerKg = 0.8;
        maxProteinPerKg = 1.8;
      }

      // Adjust for older adults
      if (inputs.age >= 60) {
        proteinPerKg = Math.max(proteinPerKg, 1.2);
        minProteinPerKg = Math.max(minProteinPerKg, 1.0);
      }

      const dailyProtein = Math.round(weightKg * proteinPerKg);
      const minProtein = Math.round(weightKg * minProteinPerKg);
      const maxProtein = Math.round(weightKg * maxProteinPerKg);
      const proteinPerLb = (proteinPerKg / 2.20462).toFixed(2);
      const perMeal = Math.round(dailyProtein / 4);
      const proteinCalories = dailyProtein * 4;
      const percentOfCalories = Math.round((proteinCalories / tdee) * 100);
      const rangePercent = ((dailyProtein - minProtein) / (maxProtein - minProtein)) * 100;

      setResults({
        dailyProtein,
        minProtein,
        maxProtein,
        proteinPerKg: proteinPerKg.toFixed(1),
        proteinPerLb,
        perMeal,
        percentOfCalories,
        rangePercent
      });
    } catch (err) {
      setError(t('errorCalculation'));
    }
  };

  const getTipsForGoal = () => {
    const goalTips = {
      loss: [
        t('tipLoss1'),
        t('tipLoss2'),
        t('tipLoss3'),
        t('tipLoss4')
      ],
      gain: [
        t('tipGain1'),
        t('tipGain2'),
        t('tipGain3'),
        t('tipGain4')
      ],
      maintain: [
        t('tipMaintain1'),
        t('tipMaintain2'),
        t('tipMaintain3'),
        t('tipMaintain4')
      ]
    };

    return goalTips[inputs.goal];
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>🥩 {t('toolTitle')}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.unitToggle}>
              <button
                type="button"
                style={styles.unitButton(unitSystem === 'imperial')}
                onClick={() => setUnitSystem('imperial')}
              >
                {t('unitImperial')}
              </button>
              <button
                type="button"
                style={styles.unitButton(unitSystem === 'metric')}
                onClick={() => setUnitSystem('metric')}
              >
                {t('unitMetric')}
              </button>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="age">{t('labelAge')}</label>
                <input
                  type="number"
                  id="age"
                  style={styles.input}
                  min="18"
                  max="80"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('labelGender')}</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioOption}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={inputs.gender === 'male'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                      style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                    />
                    <span>{t('genderMale')}</span>
                  </label>
                  <label style={styles.radioOption}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={inputs.gender === 'female'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                      style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                    />
                    <span>{t('genderFemale')}</span>
                  </label>
                </div>
              </div>
            </div>

            {unitSystem === 'imperial' ? (
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('labelHeight')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <div style={{ ...styles.inputWithUnit, flex: 1 }}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3rem' }}
                        min="3"
                        max="8"
                        value={inputs.feet}
                        onChange={(e) => setInputs({...inputs, feet: parseInt(e.target.value)})}
                        required
                      />
                      <span style={styles.inputUnit}>ft</span>
                    </div>
                    <div style={{ ...styles.inputWithUnit, flex: 1 }}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3rem' }}
                        min="0"
                        max="11"
                        value={inputs.inches}
                        onChange={(e) => setInputs({...inputs, inches: parseInt(e.target.value)})}
                        required
                      />
                      <span style={styles.inputUnit}>in</span>
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="pounds">{t('labelWeight')}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      id="pounds"
                      style={{ ...styles.input, paddingRight: '3.5rem' }}
                      min="50"
                      max="500"
                      step="0.1"
                      value={inputs.pounds}
                      onChange={(e) => setInputs({...inputs, pounds: parseFloat(e.target.value)})}
                      required
                    />
                    <span style={styles.inputUnit}>lbs</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="cm">{t('labelHeight')}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      id="cm"
                      style={{ ...styles.input, paddingRight: '3.5rem' }}
                      min="100"
                      max="250"
                      step="0.1"
                      value={inputs.cm}
                      onChange={(e) => setInputs({...inputs, cm: parseFloat(e.target.value)})}
                      required
                    />
                    <span style={styles.inputUnit}>cm</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="kg">{t('labelWeight')}</label>
                  <div style={styles.inputWithUnit}>
                    <input
                      type="number"
                      id="kg"
                      style={{ ...styles.input, paddingRight: '3.5rem' }}
                      min="30"
                      max="300"
                      step="0.1"
                      value={inputs.kg}
                      onChange={(e) => setInputs({...inputs, kg: parseFloat(e.target.value)})}
                      required
                    />
                    <span style={styles.inputUnit}>kg</span>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="activity">{t('labelActivity')}</label>
                <select
                  id="activity"
                  style={styles.select}
                  value={inputs.activity}
                  onChange={(e) => setInputs({...inputs, activity: parseFloat(e.target.value)})}
                  required
                >
                  <option value="1.2">{t('activitySedentary')}</option>
                  <option value="1.375">{t('activityLight')}</option>
                  <option value="1.465">{t('activityModerate')}</option>
                  <option value="1.55">{t('activityActive')}</option>
                  <option value="1.725">{t('activityVeryActive')}</option>
                  <option value="1.9">{t('activityExtraActive')}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="goal">{t('labelGoal')}</label>
                <select
                  id="goal"
                  style={styles.select}
                  value={inputs.goal}
                  onChange={(e) => setInputs({...inputs, goal: e.target.value as 'maintain' | 'loss' | 'gain'})}
                  required
                >
                  <option value="maintain">{t('goalMaintain')}</option>
                  <option value="loss">{t('goalLoss')}</option>
                  <option value="gain">{t('goalGain')}</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={styles.calculateButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(245, 158, 11, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>

            {error && (
              <div style={{
                background: '#fef2f2',
                color: '#991b1b',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginTop: '1rem',
                borderLeft: '4px solid #dc2626'
              }}>
                {error}
              </div>
            )}
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

              <div style={styles.resultMain}>
                <div style={styles.resultMainLabel}>{t('resultMainLabel')}</div>
                <div style={styles.resultMainValue}>{results.dailyProtein}g</div>
              </div>

              <div style={styles.resultRange}>
                <h4 style={{ color: '#374151', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                  {t('resultRangeTitle')}
                </h4>
                <div style={styles.rangeBar}>
                  <div style={styles.rangeFill(results.rangePercent)}>
                    <span>{results.minProtein}g - {results.maxProtein}g</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  <span>{results.minProtein}g</span>
                  <span>{results.maxProtein}g</span>
                </div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t('resultPerKg')}</div>
                  <div style={styles.resultCardValue}>{results.proteinPerKg}g/kg</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t('resultPerLb')}</div>
                  <div style={styles.resultCardValue}>{results.proteinPerLb}g/lb</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t('resultPerMeal')}</div>
                  <div style={styles.resultCardValue}>{results.perMeal}g</div>
                </div>

                <div style={styles.resultCard}>
                  <div style={styles.resultCardLabel}>{t('resultPercentCal')}</div>
                  <div style={styles.resultCardValue}>{results.percentOfCalories}%</div>
                </div>
              </div>

              <div style={styles.proteinTips}>
                <h4 style={{ color: '#374151', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                  💡 {t('tipsTitle')}
                </h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {getTipsForGoal().map((tip, index) => (
                    <li key={index} style={{ color: '#4b5563', marginBottom: '0.5rem', lineHeight: 1.6 }}>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li>{t('howToStep1')}</li>
          <li>{t('howToStep2')}</li>
          <li>{t('howToStep3')}</li>
          <li>{t('howToStep4')}</li>
          <li>{t('howToStep5')}</li>
          <li>{t('howToStep6')}</li>
        </ol>
      </section>

      {/* Latest Research Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>

        <h3 style={styles.h3}>{t('insightsGeneralTitle')}</h3>
        <p style={styles.paragraph}>{t('insightsGeneralText')}</p>

        <h3 style={styles.h3}>{t('insightsActiveTitle')}</h3>
        <p style={styles.paragraph}>{t('insightsActiveText')}</p>

        <h3 style={styles.h3}>{t('insightsWeightLossTitle')}</h3>
        <p style={styles.paragraph}>{t('insightsWeightLossText')}</p>

        <h3 style={styles.h3}>{t('insightsOlderTitle')}</h3>
        <p style={styles.paragraph}>{t('insightsOlderText')}</p>

        <h3 style={styles.h3}>{t('insightsClinicalTitle')}</h3>
        <p style={styles.paragraph}>{t('insightsClinicalText')}</p>
      </section>

      {/* Best Practices */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('bestPracticesTitle')}</h2>
        <ul style={styles.list}>
          <li>{t('bestPractice1')}</li>
          <li>{t('bestPractice2')}</li>
          <li>{t('bestPractice3')}</li>
          <li>{t('bestPractice4')}</li>
          <li>{t('bestPractice5')}</li>
          <li>{t('bestPractice6')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq1Question')}</h3>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq2Question')}</h3>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq3Question')}</h3>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq4Question')}</h3>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq5Question')}</h3>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faq6Question')}</h3>
          <p style={styles.paragraph}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a href="https://kefirlab.com/protein-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref1')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://legionathletics.com/protein-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref2')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://agnroots.com/blogs/articles-info/protein-calculator" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref3')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://ro.co/weight-loss/protein-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref4')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.healthkart.com/connect/protein-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref5')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.calculator.net/protein-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref6')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.loopprotein.com/calculators/protein-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('ref7')}
          </a>
        </div>
      </section>
    </div>
  );
}